import { useEffect, useMemo, useState } from "react";

export default function AdminNotifications() {
  const [loading, setLoading] = useState(true);
  const [savingSeen, setSavingSeen] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  async function loadNotifications() {
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/.netlify/functions/get-notifications", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load notifications");
      }

      setItems(Array.isArray(data.items) ? data.items : []);
      setLastSeenAt(data.lastSeenAt || null);
      setSelectedIds([]);
    } catch (err) {
      setError(err.message || "Failed to load notifications");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNotifications();
  }, []);

  const newCount = useMemo(
    () => items.filter((item) => item.isNew).length,
    [items]
  );
  const seenItems = useMemo(
    () => items.filter((item) => !item.isNew),
    [items]
  );
  const selectedSeenIds = useMemo(
    () => selectedIds.filter((id) => seenItems.some((item) => item.id === id)),
    [selectedIds, seenItems]
  );

  function toggleSelected(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  }

  function toggleSelectAllSeen() {
    if (selectedSeenIds.length === seenItems.length && seenItems.length > 0) {
      setSelectedIds((prev) =>
        prev.filter((id) => !seenItems.some((item) => item.id === id))
      );
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      seenItems.forEach((item) => next.add(item.id));
      return [...next];
    });
  }

  async function markSeen() {
    setSavingSeen(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/mark-notifications-seen", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to mark notifications as seen");
      }
      setLastSeenAt(data.lastSeenAt || null);
      setItems((prev) => prev.map((item) => ({ ...item, isNew: false })));
    } catch (err) {
      setError(err.message || "Failed to mark notifications as seen");
    } finally {
      setSavingSeen(false);
    }
  }

  async function deleteSelected() {
    if (!selectedSeenIds.length) return;

    setDeletingSelected(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/delete-notifications", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedSeenIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to delete notifications");
      }

      setItems((prev) => prev.filter((item) => !selectedSeenIds.includes(item.id)));
      setSelectedIds((prev) => prev.filter((id) => !selectedSeenIds.includes(id)));
    } catch (err) {
      setError(err.message || "Failed to delete notifications");
    } finally {
      setDeletingSelected(false);
    }
  }

  return (
    <div className="admin-events">
      <h1>Notifications</h1>

      <div className="ae-toolbar ae-notifications-toolbar">
        <p className="ae-hint ae-notifications-status">
          {newCount > 0 ? `${newCount} new` : "No new notifications"}
          {lastSeenAt
            ? ` • Last seen ${new Date(lastSeenAt).toLocaleString()}`
            : ""}
        </p>
        <button
          type="button"
          className="ae-button ae-button-outline"
          onClick={loadNotifications}
          disabled={loading || savingSeen}
        >
          Refresh
        </button>
        <button
          type="button"
          className="ae-button ae-button-primary"
          onClick={markSeen}
          disabled={savingSeen || items.length === 0}
        >
          {savingSeen ? "Saving..." : "Mark all as seen"}
        </button>
        <button
          type="button"
          className="ae-button ae-button-outline"
          onClick={toggleSelectAllSeen}
          disabled={loading || deletingSelected || seenItems.length === 0}
        >
          {selectedSeenIds.length === seenItems.length && seenItems.length > 0
            ? "Clear selection"
            : "Select all seen"}
        </button>
        <button
          type="button"
          className="ae-button ae-button-danger-soft"
          onClick={deleteSelected}
          disabled={deletingSelected || selectedSeenIds.length === 0}
        >
          {deletingSelected ? "Deleting..." : `Delete selected${selectedSeenIds.length ? ` (${selectedSeenIds.length})` : ""}`}
        </button>
      </div>

      {loading && <p className="ae-hint">Loading notifications...</p>}
      {!loading && error && <p className="ae-error">{error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="ae-hint">No activity yet.</p>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="ae-notification-list">
          {items.map((item) => (
            <article
              key={item.id}
              className={`ae-notification-card ${item.isNew ? "is-new" : ""}`}
            >
              <label className="ae-notification-select">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={() => toggleSelected(item.id)}
                  disabled={item.isNew || deletingSelected}
                  aria-label={`Select notification ${item.title}`}
                />
              </label>
              <div className="ae-notification-main">
                <div className="ae-notification-top">
                  <strong>{item.title}</strong>
                  {item.isNew ? <span className="ae-pill-new">New</span> : null}
                </div>
                <span>{item.subtitle}</span>
                <small>
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleString()
                    : "Unknown time"}
                </small>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
